import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../services/analyticsService';
import { logUiEvent, startUiTrail } from '../services/uiTrailService';

/**
 * Fires a Firebase Analytics `page_view` on every route change so we can see
 * where users land, where they drop off, and which surfaces drive retention.
 * Renders nothing. Mount once inside the Router. No-ops when analytics is off
 * (see analyticsService — guarded by the `analytics` instance).
 *
 * IT ALSO WRITES NAVIGATION INTO THE UI TRAIL (TRAIL-b, 2026-09-13), which is a different job for a different
 * reader. Analytics answers "where do people go" in aggregate and is PII-free by rule; the trail answers "what
 * did THIS session do, in order" so a reported problem can be replayed. Same screen normalization for both,
 * because an id in a path is noise to one and a privacy question to the other.
 */
const RouteAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Strip ID-like path segments so views aggregate per screen, not per record.
    const screen = location.pathname
      .replace(/\/[0-9a-fA-F-]{8,}/g, '/:id')
      .replace(/\/\d+/g, '/:id') || '/';
    trackPageView(screen);
    logUiEvent('nav', { route: screen });
  }, [location.pathname]);

  // One listener for the life of the app: flush the queue when the tab is hidden, because the last events
  // before somebody closes it are usually the interesting ones.
  useEffect(() => { startUiTrail(); }, []);

  return null;
};

export default RouteAnalytics;
